import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { Expand, Minimize } from 'lucide-react';
import type { WidgetData } from '@auw/workspace-utils';

import { useOptionalPlatform } from '@auw/platform-contracts';
import shellHtml from './widget-shell.html?raw';
import { collectWidgetFontFaces } from './widgetFonts';

interface WidgetRendererProps {
  widget: WidgetData;
}

export default function WidgetRenderer({ widget }: WidgetRendererProps) {
  const sandboxUrl = useOptionalPlatform()?.widgetSandboxUrl;
  const [expanded, setExpanded] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(Math.min(widget.height, 600));
  const [isLoaded, setIsLoaded] = useState(false);
  const sentRef = useRef<{ code: string; isSvg: boolean } | null>(null);

  const handleMessage = useCallback(
    (e: MessageEvent) => {
      if (e.data?.type === 'widget-resize' && typeof e.data.height === 'number') {
        if (iframeRef.current && e.source === iframeRef.current.contentWindow) {
          const newHeight = Math.max(100, Math.min(e.data.height, 5000));
          setHeight(newHeight);
        }
      }
    },
    [],
  );

  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleMessage]);

  /* The sandbox cannot fetch a webfont (`font-src 'none'` + opaque origin),
   * so ship it the Pretendard subsets the app already loaded. Resolving the
   * bytes is async, hence a separate message from `set-content`: the widget
   * paints in system fonts and swaps once the faces land. */
  const sendFonts = useCallback((frame: Window, html: string) => {
    void collectWidgetFontFaces(html).then((faces) => {
      if (faces.length === 0) return;
      if (iframeRef.current?.contentWindow !== frame) return;
      frame.postMessage({ type: 'set-fonts', faces }, '*');
    });
  }, []);

  const handleLoad = useCallback(() => {
    const frame = iframeRef.current?.contentWindow;
    if (!frame) return;
    frame.postMessage(
      { type: 'set-content', html: widget.widgetCode, isSvg: widget.isSvg, runScripts: true }, '*',
    );
    sentRef.current = { code: widget.widgetCode, isSvg: widget.isSvg };
    setIsLoaded(true);
    sendFonts(frame, widget.widgetCode);
  }, [widget.widgetCode, widget.isSvg, sendFonts]);

  useEffect(() => {
    const frame = iframeRef.current?.contentWindow;
    if (!isLoaded || !frame) return;
    if (sentRef.current?.code === widget.widgetCode && sentRef.current.isSvg === widget.isSvg) return;
    sentRef.current = { code: widget.widgetCode, isSvg: widget.isSvg };
    frame.postMessage(
      { type: 'set-content', html: widget.widgetCode, isSvg: widget.isSvg, runScripts: true },
      '*',
    );
    // Streaming edits can introduce characters no earlier subset covered.
    sendFonts(frame, widget.widgetCode);
  }, [widget.widgetCode, widget.isSvg, isLoaded, sendFonts]);

  useEffect(() => {
    if (!expanded) return;
    const close = (event: KeyboardEvent) => { if (event.key === 'Escape') setExpanded(false); };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [expanded]);

  const content = (
    <Container $expanded={expanded} role={expanded ? 'dialog' : undefined} aria-label={expanded ? widget.title : undefined}>
      <Header>
        <HeaderLeft>
          <HeaderIcon>{widget.isSvg ? '◇' : '⬡'}</HeaderIcon>
          <HeaderTitle>{widget.title.replace(/_/g, ' ')}</HeaderTitle>
        </HeaderLeft>
        <PopoutBtn onClick={() => setExpanded((value) => !value)} title={expanded ? "위젯 축소" : "위젯 확대"} aria-label={expanded ? "위젯 축소" : "위젯 확대"}>
          {expanded ? <Minimize size={14} /> : <Expand size={14} />}
        </PopoutBtn>
      </Header>
      <IframeWrapper $height={height} $expanded={expanded}>
        <StyledIframe
          ref={iframeRef}
          src={sandboxUrl}
          srcDoc={sandboxUrl ? undefined : shellHtml}
          sandbox="allow-scripts"
          title={widget.title}
          onLoad={handleLoad}
          $height={height}
          $expanded={expanded}
        />
      </IframeWrapper>
    </Container>
  );
  // Chat bubbles can establish a fixed-position containing block. Mount the
  // expanded view at the document root so it occupies the actual viewport.
  return expanded ? createPortal(content, document.body) : content;
}

const Container = styled.div<{ $expanded: boolean }>`
  ${(p) => p.$expanded && `position: fixed; inset: 24px; z-index: 10000; display: flex; flex-direction: column; box-shadow: 0 8px 48px rgba(0,0,0,0.3);`}
  margin: 12px 0;
  border: 0.5px solid var(--color-border-tertiary, rgba(0, 0, 0, 0.15));
  border-radius: 12px;
  overflow: hidden;
  background: var(--color-bg-primary, #fff);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 14px;
  border-bottom: 0.5px solid var(--color-border-tertiary, rgba(0, 0, 0, 0.1));
  background: var(--color-bg-secondary, #f5f5f5);
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const HeaderIcon = styled.span`
  font-size: 13px;
  opacity: 0.6;
`;

const HeaderTitle = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: var(--color-text-secondary, #666);
  text-transform: capitalize;
`;

const PopoutBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: var(--color-text-tertiary, #999);
  cursor: pointer;
  flex-shrink: 0;
  transition: color 0.15s, background 0.15s;

  &:hover {
    color: var(--color-text-primary, #1a1a1a);
    background: rgba(0, 0, 0, 0.06);
  }
`;

const IframeWrapper = styled.div<{ $height: number; $expanded: boolean }>`
  ${(p) => p.$expanded && `flex: 1; min-height: 0;`}
  width: 100%;
  height: ${(p) => p.$expanded ? '100%' : `${p.$height}px`};
  transition: height 0.2s ease;
  overflow: hidden;
`;

const StyledIframe = styled.iframe<{ $height: number; $expanded: boolean }>`
  width: 100%;
  height: ${(p) => p.$expanded ? '100%' : `${p.$height}px`};
  border: none;
  display: block;
`;
